import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { ErroDaApi } from '../../../../api/client'
import { useRegistrarMarcacao } from '../../../../api/queries/chamados'
import { Botao } from '../../../../components/Botao'
import { AvisoDeErro } from '../../../../components/Estados'
import { TEXTOS } from '../../../../lib/glossario'
import type { MarcacaoNaTela, ParecerDaIa } from '../../../../types/chamado'
import { CamposDoJulgamento } from './CamposDoJulgamento'
import { deMarcacao, esquemaDeMarcacao, paraRequisicao, type FormularioDeMarcacao as Valores } from './esquema'
import { Julgamento } from './LeituraDoJulgamento'
import { camposAceitos, comPropostas, type CampoProponivel } from './proposta'
import { ResumoDaSugestao } from './ResumoDaSugestao'

interface Props {
  chamadoId: number
  /** Mês de referência do chamado, para a tela dizer onde a análise vai contar. */
  competencia: string | null
  marcacao: MarcacaoNaTela | null
  /** O parecer da IA exibido no mesmo cartão, quando há um. */
  parecer: ParecerDaIa | null
  aoConcluir: () => void
  aoCancelar: () => void
}

/**
 * O formulário do julgamento.
 *
 * O aviso de precedência fica acima dos botões, e não escondido numa ajuda: salvar aqui muda quem
 * manda naquele chamado dali em diante — a carga de planilha passa a preservar esta análise em vez
 * de sobrescrevê-la. É uma porta de mão única, e quem clica precisa saber disso antes.
 *
 * @param props o chamado, o julgamento atual e o que fazer ao terminar
 * @returns o formulário
 */
export function FormularioDeMarcacao({
  chamadoId,
  competencia,
  marcacao,
  parecer,
  aoConcluir,
  aoCancelar,
}: Props) {
  const gravar = useRegistrarMarcacao(chamadoId)
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<Valores>({
    resolver: zodResolver(esquemaDeMarcacao),
    // As sugestões da IA entram como valor inicial APENAS onde o formulário abriria vazio — nunca
    // por cima do que o fiscal já registrou. E elas nunca alcançam conformidade e aceite, porque a
    // IA não os propõe: as duas perguntas em que responder em branco seria aprovar continuam
    // exigindo resposta humana, que é o princípio que este formulário nasceu defendendo.
    defaultValues: comPropostas(deMarcacao(marcacao), parecer),
  })
  const valores = watch()
  // O código, nunca o texto: a tela reage a `JULGAMENTO_DESATUALIZADO` mostrando o julgamento
  // vigente, e a nenhuma outra falha.
  const conflito = gravar.error instanceof ErroDaApi && gravar.error.codigo === 'JULGAMENTO_DESATUALIZADO'

  /** Põe a sugestão no campo, como se a pessoa tivesse clicado nela. Não salva. */
  function usarSugestao(campo: CampoProponivel, valor: string) {
    const alvo = campo === 'criticidade' ? 'criticidadeTis' : campo
    setValue(alvo, valor as never, { shouldDirty: true, shouldValidate: true })
  }

  async function salvar(valores: Valores) {
    // `mutateAsync` com try/catch, e não `mutate`: sem o catch, a promessa rejeitada vira erro não
    // tratado no console e a tela fecha como se tivesse salvo. O erro em si já está em `gravar`.
    try {
      // A trilha da V20 vai junto: qual parecer estava na tela e QUAIS CAMPOS o fiscal manteve
      // como propostos. Nomes, nunca valores — o que o servidor grava é o formulário, e só.
      await gravar.mutateAsync({
        ...paraRequisicao(valores, marcacao),
        parecerIaId: parecer?.id ?? null,
        camposAceitosDaIa: camposAceitos(valores, parecer),
      })
      aoConcluir()
    } catch {
      // O `AvisoDeErro` abaixo mostra o que o servidor respondeu.
    }
  }

  return (
    <form onSubmit={handleSubmit(salvar)} className="space-y-5">
      {parecer && (
        <p className="max-w-prose rounded-lg border border-niteroi-200 bg-niteroi-50/60 px-3 py-2 text-xs text-slate-700">
          {TEXTOS.marcacao.sugestaoDaIa.comoUsar}
        </p>
      )}

      <ResumoDaSugestao parecer={parecer} aoUsar={usarSugestao} />

      <CamposDoJulgamento
        register={register}
        errors={errors}
        corretiva={valores.natureza === 'CORRETIVA'}
        competencia={competencia}
        parecer={parecer}
        valores={valores}
      />

      <p className="max-w-prose rounded-lg border border-laranja-200 bg-laranja-50 px-3 py-2 text-xs text-slate-700">
        {TEXTOS.marcacao.avisoDePrecedencia}
      </p>

      {gravar.isError && (
        <AvisoDeErro
          erro={gravar.error}
          titulo={conflito ? TEXTOS.marcacao.conflito.titulo : TEXTOS.marcacao.naoSalvou}
        />
      )}

      {/* O julgamento vigente ao lado do que a pessoa digitou — que é o único jeito de ela decidir
          o que fazer. `marcacao` já vem recarregado: a mutação invalida o detalhe quando falha,
          justamente para esta seção não mostrar a versão velha, que é o problema e não a resposta. */}
      {conflito && marcacao && (
        <section
          className="max-w-prose rounded-lg border border-amber-300 bg-amber-50 p-3"
          aria-label={TEXTOS.marcacao.conflito.vigente}
        >
          <h3 className="text-sm font-semibold text-amber-900">{TEXTOS.marcacao.conflito.vigente}</h3>
          <p className="mt-1 text-xs text-amber-900">{TEXTOS.marcacao.conflito.comoSeguir}</p>
          <div className="mt-3">
            <Julgamento marcacao={marcacao} categoriaDoENiteroi={null} />
          </div>
        </section>
      )}

      <div className="flex flex-wrap gap-2">
        <Botao type="submit" aparencia="primario" disabled={gravar.isPending}>
          {gravar.isPending ? TEXTOS.marcacao.salvando : TEXTOS.marcacao.salvar}
        </Botao>
        <Botao type="button" onClick={aoCancelar} disabled={gravar.isPending}>
          {TEXTOS.marcacao.cancelar}
        </Botao>
      </div>
    </form>
  )
}
